import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getRequest, postRequest } from "../services";
import toast from "react-hot-toast";

const initialState = {
  enrolledCourses: [],
  allCourseEnrollments: [],
  loading: false,
  error: null,
};

export const enrollStudent = createAsyncThunk(
  "courseEnrollment/enrollStudent",
  async ({ courseId, studentId }, { rejectWithValue }) => {
    try {
      const response = await postRequest(
        `/api/Course/enroll?courseId=${courseId}&studentId=${studentId}`
      );
      if (response.status !== 200) {
        return rejectWithValue("Failed to enroll student.");
      }
      toast.success("Student enrolled successfully!");
      return { courseId, studentId };
    } catch (error) {
      toast.error(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllCourseEnrollments = createAsyncThunk(
  "courseEnrollment/fetchAllCourseEnrollments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest(`/api/Course/GetCourseEnrollments`);
      if (response.status !== 200) {
        return rejectWithValue("Failed to fetch course enrollments.");
      }
      
      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchEnrolledCourses = createAsyncThunk(
  "courseEnrollment/fetchEnrolledCourses",
  async (studentId, { getState, rejectWithValue }) => {
    try {
      // First check if we already have all enrollments loaded
      const { allCourseEnrollments } = getState().courseEnrollment;
      
      // If we have enrollments, filter for this student
      if (allCourseEnrollments.length > 0) {
        return allCourseEnrollments.filter(
          enrollment => enrollment.studentId === studentId
        );
      }
      
      // Otherwise fetch all enrollments and then filter
      const response = await getRequest(`/api/Course/GetCourseEnrollments`);
      if (response.status !== 200) {
        return rejectWithValue("Failed to fetch enrolled courses.");
      }
      
      const allEnrollments = response.data?.data || [];
      return allEnrollments.filter(
        enrollment => enrollment.studentId === studentId
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const courseEnrollmentSlice = createSlice({
  name: "courseEnrollment",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(enrollStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enrollStudent.fulfilled, (state, action) => {
        state.loading = false;
        // Only add the enrollment if it's not already in the list
        if (!state.enrolledCourses.some(
          course => course.courseId === action.payload.courseId && 
                    course.studentId === action.payload.studentId
        )) {
          state.enrolledCourses.push(action.payload);
        }
      })
      .addCase(enrollStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllCourseEnrollments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCourseEnrollments.fulfilled, (state, action) => {
        state.loading = false;
        state.allCourseEnrollments = action.payload;
      })
      .addCase(fetchAllCourseEnrollments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.allCourseEnrollments = []; // Clear enrollments on error
      })
      .addCase(fetchEnrolledCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnrolledCourses.fulfilled, (state, action) => {
        state.loading = false;
        state.enrolledCourses = action.payload;
      })
      .addCase(fetchEnrolledCourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.enrolledCourses = []; // Clear courses on error
      });
  },
});

export const { clearError } = courseEnrollmentSlice.actions;
export default courseEnrollmentSlice.reducer;